/**
 * Onboarding Candidate View Page — read-only detail with status advancement.
 */

import * as React from 'react';
import { useParams, useNavigate } from 'react-router';
import { Button, Card, CardContent, CardHeader, CardTitle, Badge } from '@yesbos/ui';
import { ArrowLeft, Edit } from 'lucide-react';

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL ?? 'http://localhost:9200';

interface Candidate {
  id: string;
  full_name: string;
  email?: string;
  phone?: string;
  position_applied: string;
  status: string;
  expected_join_date?: string;
  offered_ctc?: number;
  source?: string;
  notes?: string;
  department_name?: string;
  designation_name?: string;
  created_at?: string;
}

const STATUS_BADGE_CLASS: Record<string, string> = {
  applied: 'bg-blue-100 text-blue-700',
  screening: 'bg-yellow-100 text-yellow-700',
  offered: 'bg-purple-100 text-purple-700',
  accepted: 'bg-green-100 text-green-700',
  joining: 'bg-teal-100 text-teal-700',
  joined: 'bg-emerald-100 text-emerald-700',
  withdrawn: 'bg-red-100 text-red-700',
};

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <dt className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</dt>
      <dd className="text-sm font-medium">{value ?? '—'}</dd>
    </div>
  );
}

async function callBLL(path: string, method = 'GET', body?: unknown) {
  const token = localStorage.getItem('axon_token') ?? '';
  const res = await fetch(`${GATEWAY_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return res.json();
}

export function OnboardingViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [candidate, setCandidate] = React.useState<Candidate | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (!id) return;
    setLoading(true);
    callBLL(`/v1/hros/onboarding/candidates/${id}`)
      .then((res) => {
        if (res.success && res.data) {
          setCandidate(res.data as Candidate);
        } else {
          setError(res.error?.message ?? 'Candidate not found.');
        }
      })
      .catch(() => setError('Network error.'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading...</div>;
  if (error) return <div className="p-6 text-sm text-destructive">{error}</div>;
  if (!candidate) return null;

  const statusClass = STATUS_BADGE_CLASS[candidate.status] ?? 'bg-gray-100 text-gray-700';

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/onboarding')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold">{candidate.full_name}</h1>
            <p className="text-sm text-muted-foreground">{candidate.position_applied}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
            {candidate.status.charAt(0).toUpperCase() + candidate.status.slice(1)}
          </span>
          <Button variant="outline" size="sm" onClick={() => navigate(`/onboarding/${id}/edit`)}>
            <Edit className="h-4 w-4 mr-2" />Edit
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle>Contact Information</CardTitle></CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-4">
            <Field label="Email" value={candidate.email} />
            <Field label="Phone" value={candidate.phone} />
            <Field label="Source" value={candidate.source} />
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Offer Details</CardTitle></CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-4">
            <Field label="Expected Join Date" value={candidate.expected_join_date ? new Date(candidate.expected_join_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : undefined} />
            <Field label="Offered CTC" value={candidate.offered_ctc != null ? `₹${candidate.offered_ctc.toLocaleString('en-IN')}` : undefined} />
            <Field label="Department" value={candidate.department_name} />
            <Field label="Designation" value={candidate.designation_name} />
          </dl>
        </CardContent>
      </Card>

      {candidate.notes && (
        <Card>
          <CardHeader><CardTitle>Notes</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{candidate.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default OnboardingViewPage;
